import React, { useMemo, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { loginErrorMessage } from '@/utils/loginErrorMessage';
import { passwordResetHandoff } from '@/utils/passwordResetRequired';
import { useT } from '@/i18n/labels';
import { useThemeStore } from '@/stores/themeStore';
import { dirFor } from '@/stores/langStore';
import { makeTokens } from '@/theme/tokens';

// Plain-text nav link under the submit button. Inline style (not a new CSS
// class) — matches the ForgotPasswordPage/ResetPasswordPage link styling.
const forgotLinkStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  color: 'var(--accent)',
  fontSize: 13.5,
  fontWeight: 600,
  cursor: 'pointer',
  textDecoration: 'underline',
};

/**
 * Login page for agent authentication. POST /api/identity/login with
 * username, password, tenantId. Styled with the redesign token system —
 * it renders outside <ConsoleShell/>, so it applies tokens to its own root.
 */
export const LoginPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  // `?tenant=` prefills the company code, same convention as
  // ForgotPasswordPage. The marketing site's /login page collects the code and
  // hands it over here; it is an identifier, not a secret. Username and
  // password are never accepted from the query string.
  const [tenantId, setTenantId] = useState(searchParams.get('tenant') ?? '');
  const [error, setError] = useState('');
  // Set by /auth/handoff when a marketing-site sign-in code was missing, used or expired.
  const handoffExpired = searchParams.get('handoff') === 'expired';
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, lang } = useT();
  const mode = useThemeStore((s) => s.mode);
  const tokens = useMemo(() => makeTokens({ mode }), [mode]);

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname ?? '/';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login({ email: username, password, tenantId });
      navigate(from, { replace: true });
    } catch (err) {
      // Valid credentials on a temporary-password account: the server answers
      // 403 password_reset_required with NO token. Not an error to display —
      // send the user to set a new password (same flow as admin-portal).
      const handoff = passwordResetHandoff(err, { email: username, password, tenantId });
      if (handoff) {
        navigate('/change-password', { state: handoff });
        return;
      }
      setError(loginErrorMessage(err, lang));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ac-login" dir={dirFor(lang)} style={tokens as React.CSSProperties}>
      <form onSubmit={handleSubmit} className="ac-card ac-login-card" data-testid="login-form">
        <div className="ac-login-brand">
          <div className="ac-rail-tenant" style={{ width: 40, height: 40, fontSize: 17 }}>
            B
          </div>
          <div>
            <div className="ac-login-title">{t('appTitle')}</div>
          </div>
        </div>
        <div className="ac-login-sub">{t('poweredBy')}</div>

        {handoffExpired && !error && (
          <div role="status" dir="auto" className="ac-login-sub" style={{ marginBottom: 14 }}>
            {t('loginHandoffExpired')}
          </div>
        )}

        {error && (
          <div role="alert" dir="auto" className="ac-login-err">
            {error}
          </div>
        )}

        <div className="ac-set-field-group" style={{ marginBottom: 14 }}>
          <label htmlFor="tenantId" className="ac-set-field-label">
            {t('loginCompanyCode')}
          </label>
          <input
            id="tenantId"
            type="text"
            className="ac-field"
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            required
          />
        </div>

        <div className="ac-set-field-group" style={{ marginBottom: 14 }}>
          <label htmlFor="username" className="ac-set-field-label">
            {t('loginUsername')}
          </label>
          <input
            id="username"
            type="text"
            className="ac-field"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            autoComplete="username"
          />
        </div>

        <div className="ac-set-field-group" style={{ marginBottom: 22 }}>
          <label htmlFor="password" className="ac-set-field-label">
            {t('loginPassword')}
          </label>
          <input
            id="password"
            type="password"
            className="ac-field"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
          />
        </div>

        <button type="submit" disabled={loading} className="ac-btn primary" style={{ width: '100%' }}>
          {loading && <LoadingSpinner size={18} />}
          {loading ? t('loginSigningIn') : t('loginSignIn')}
        </button>

        <p style={{ marginTop: 14, marginBottom: 0, textAlign: 'center' }}>
          <button type="button" onClick={() => navigate('/forgot-password')} style={forgotLinkStyle}>
            {t('loginForgotPassword')}
          </button>
        </p>
      </form>
    </div>
  );
};
